// File: src/pages/Owner/OwnerMissedPunchApproval.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 * ORIGINAL BUG:
 *   - loadRequests() called GET /missed-punch/manager  ← WRONG for Owner
 *   - filter: r.status === 'PENDING' only — misses PENDING_MANAGER records
 *   - No approval manager remark column
 *
 * NEW (ALL CHANGES MARKED // NEW):
 *   - BUG FIX: loadRequests() now calls GET /missed-punch/owner  ← CORRECT
 *     The owner endpoint returns records at PENDING_MANAGER or PENDING
 *     stage that are under owner's company, not scoped to a manager's branches
 *   - filter: includes PENDING, PENDING_MANAGER, and MANAGER_APPROVED
 *     (owner can do final audit on manager-approved records too)
 *   - NEW column: "AM Remark (View Only)" — shows approvalManagerRemarks
 *     as yellow VIEW ONLY chip with full text tooltip
 *   - Table now shows approvalManagerApprovedBy name when present
 *   - Status chip shows human-readable label
 * =====================================================================
 */
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableHead, TableRow,
    TableCell, TableBody, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Stack, Tooltip
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const statusColor = (s) => {
    if (!s) return 'default';
    const u = s.toUpperCase();
    if (u.includes('APPROVED')) return 'success';
    if (u.includes('REJECTED')) return 'error';
    if (u.includes('PENDING')) return 'warning';
    return 'default';
};

const statusLabel = (s) => {
    const map = {
        PENDING: 'Pending',
        PENDING_MANAGER: 'Pending Mgr',
        PENDING_APPROVAL_MANAGER: 'Pending AM',
        MANAGER_APPROVED: 'Mgr Approved',
    };
    return map[s] || s;
};

const OwnerMissedPunchApproval = () => {
    const [requests, setRequests] = useState([]);
    const [open, setOpen] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [action, setAction] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    /* ================= LOAD OWNER PENDING ================= */
    const loadRequests = async () => {
        try {
            // NEW BUG FIX: Was /missed-punch/manager — WRONG for owner
            // Owner sees all PENDING_MANAGER requests across ALL branches in their company
            const res = await api.get('/missed-punch/owner');
            setRequests(res.data || []);
        } catch (err) {
            console.error('Failed to load requests for owner', err);
            toast.error('Failed to load missed punch requests');
        }
    };

    useEffect(() => { loadRequests(); }, []);

    const openDialog = (id, type) => {
        setSelectedId(id);
        setAction(type);
        setRemarks('');
        setOpen(true);
    };

    const submitAction = async () => {
        if (!remarks.trim()) {
            toast.error('Remarks are required');
            return;
        }
        try {
            const url = action === 'APPROVE'
                ? `/missed-punch/owner/approve/${selectedId}`
                : `/missed-punch/owner/reject/${selectedId}`;

            await api.post(url, null, { params: { remarks } });
            toast.success(action === 'APPROVE' ? 'Approved' : 'Rejected');
            setOpen(false);
            loadRequests();
        } catch (err) {
            toast.error('Action failed: ' + (err?.response?.data?.message || err.message));
        }
    };

    // NEW: Include PENDING and PENDING_MANAGER (escalated from AM included)
    const pendingRequests = requests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'PENDING' || s === 'PENDING_MANAGER';
    });

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight={800} mb={3}>
                Missed Punch Approvals (Owner Audit)
            </Typography>

            <Paper sx={{ borderRadius: 3, overflowX: 'auto' }}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                        <TableRow>
                            <TableCell><b>Employee</b></TableCell>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Punch Type</b></TableCell>
                            <TableCell><b>Requested Time</b></TableCell>
                            <TableCell><b>Reason</b></TableCell>
                            {/* NEW column */}
                            <TableCell>
                                <b>AM Remark</b>
                                <Typography component="span" fontSize={10} color="text.secondary"> (View Only)</Typography>
                            </TableCell>
                            <TableCell><b>Status</b></TableCell>
                            <TableCell><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {pendingRequests.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No pending requests
                                </TableCell>
                            </TableRow>
                        )}

                        {pendingRequests.map(r => (
                            <TableRow key={r.id || r.attendanceId} hover>
                                <TableCell>
                                    <b>{r.employee?.name || r.employeeName}</b>
                                    <br />
                                    <small style={{ color: '#64748b' }}>
                                        {r.employee?.employeeCode || '-'}
                                    </small>
                                </TableCell>

                                <TableCell>{r.attendanceDate || r.date}</TableCell>
                                <TableCell>{r.punchType}</TableCell>
                                <TableCell>{r.requestedClockIn || r.requestedClockOut || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 160 }}>
                                    <Typography fontSize={13}>{r.reason}</Typography>
                                </TableCell>

                                {/* NEW: AM remark VIEW ONLY */}
                                <TableCell sx={{ maxWidth: 180 }}>
                                    {r.approvalManagerRemarks ? (
                                        <Tooltip title={r.approvalManagerRemarks} arrow placement="top">
                                            <Box>
                                                <Chip
                                                    size="small"
                                                    icon={<InfoOutlined sx={{ fontSize: 13 }} />}
                                                    label="View AM Note"
                                                    sx={{
                                                        bgcolor: '#fef9c3',
                                                        color: '#854d0e',
                                                        border: '1px solid #fde047',
                                                        fontWeight: 700,
                                                        fontSize: 11,
                                                        cursor: 'help'
                                                    }}
                                                />
                                                {r.approvalManagerApprovedBy && (
                                                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                                                        by {r.approvalManagerApprovedBy}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Typography fontSize={12} color="text.disabled">—</Typography>
                                    )}
                                </TableCell>

                                <TableCell>
                                    <Chip
                                        label={statusLabel(r.status)}
                                        color={statusColor(r.status)}
                                        size="small"
                                    />
                                </TableCell>

                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" color="success" variant="contained"
                                            onClick={() => openDialog(r.id || r.attendanceId, 'APPROVE')}>
                                            Approve
                                        </Button>
                                        <Button size="small" color="error" variant="outlined"
                                            onClick={() => openDialog(r.id || r.attendanceId, 'REJECT')}>
                                            Reject
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* ================= REMARKS DIALOG ================= */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {action === 'APPROVE' ? 'Approve Request' : 'Reject Request'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Remarks"
                        fullWidth multiline minRows={3}
                        value={remarks}
                        onChange={e => setRemarks(e.target.value)}
                        autoFocus
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={action === 'APPROVE' ? 'success' : 'error'}
                        onClick={submitAction}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OwnerMissedPunchApproval;